import React, { useEffect, useMemo, useState } from "react";
import { Presentation } from "lucide-react";
import BookmarkGroupsBrowser, {
  type BookmarkCardRenderArgs,
} from "./BookmarkGroupsBrowser";
import BookmarkManagementCard from "./BookmarkManagementCard";
import ModalConfirm from "./ModalConfirm";
import {
  buildBookmarkBookMetaById,
  buildBookmarkGroups,
  DUPLICATE_GROUP_WARNING_TEXT,
  isSupportedBook,
} from "../utils/bookmarkGroupData";
import useCtx from "../hooks/useCts";
import {
  deleteElectronicBookmark,
  ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_ERROR,
  ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_TEXT,
  getClientElectronicBookmarks,
  isElectronicBookmarkGroupNameTooLong,
  normalizeGroupName,
  renameElectronicBookmark,
  upsertElectronicBookmark,
} from "../api/editGropus";
import { blurActiveElement } from "../utils/helpers";

type SelectedBookmarkState = {
  bookId: string | null;
  groupName: string;
};

const EMPTY_GROUP_NAME_WARNING_TEXT = "Wpisz nazwę grupy.";

const Groups: React.FC = () => {
  const ctx = useCtx();
  const [bookmarks, setBookmarks] = useState<ElectronicBookmark[]>([]);
  const [isLoadingBookmarks, setIsLoadingBookmarks] = useState(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [bookmarkMessage, setBookmarkMessage] = useState<{
    type: "success" | "error" | "info" | "warning";
    text: string;
  } | null>(null);
  const [selectedBookmark, setSelectedBookmark] =
    useState<SelectedBookmarkState>({
      bookId: null,
      groupName: "",
    });
  const [editingBookmarkId, setEditingBookmarkId] = useState<string | null>(
    null,
  );
  const [renameDraft, setRenameDraft] = useState("");
  const [pendingRenameId, setPendingRenameId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [bookmarkPendingDelete, setBookmarkPendingDelete] =
    useState<ElectronicBookmark | null>(null);

  const userClient = useMemo(
    () => ctx.clients.find((client) => client.authId === ctx.user?.uid) ?? null,
    [ctx.clients, ctx.user?.uid],
  );

  const books = useMemo<(BookNew | KidsBook)[]>(
    () => [...(ctx.books ?? []), ...(ctx.booksKids ?? [])],
    [ctx.books, ctx.booksKids],
  );

  const sortedBooks = useMemo(
    () => [
      ...books
        .filter((book): book is BookNew => "bookNum" in book)
        .sort((a, b) => a.bookNum - b.bookNum),
      ...books
        .filter((book): book is KidsBook => "num" in book)
        .sort((a, b) => a.num - b.num),
    ],
    [books],
  );

  const visibleBooks = useMemo(
    () => sortedBooks.filter(isSupportedBook),
    [sortedBooks],
  );

  const purchasedBookIds = useMemo(
    () =>
      [
        ...(ctx.books ?? [])
          .filter((book) => userClient?.orderedBooks?.includes(book.id ?? ""))
          .map((book) => book.id),
        ...(ctx.booksKids ?? [])
          .filter((book) => userClient?.orderedBooks?.includes(book.id ?? ""))
          .map((book) => book.id),
      ].filter(Boolean) as string[],
    [ctx.books, ctx.booksKids, userClient?.orderedBooks],
  );

  const bookMetaById = useMemo(
    () => buildBookmarkBookMetaById(visibleBooks),
    [visibleBooks],
  );

  const bookmarkGroups = useMemo(
    () =>
      buildBookmarkGroups({
        visibleBooks,
        purchasedBookIds,
        bookmarks,
        bookMetaById,
      }),
    [visibleBooks, purchasedBookIds, bookmarks, bookMetaById],
  );

  const clearRenameState = () => {
    blurActiveElement();
    setEditingBookmarkId(null);
    setRenameDraft("");
  };

  useEffect(() => {
    const loadBookmarks = async () => {
      if (!userClient?.id) {
        setBookmarks([]);
        return;
      }

      setIsLoadingBookmarks(true);

      try {
        const loadedBookmarks = await getClientElectronicBookmarks(
          userClient.id,
        );
        setBookmarks(loadedBookmarks);
      } catch (error) {
        console.error("Błąd pobierania zakładek elektronicznych:", error);
        setBookmarkMessage({
          type: "error",
          text: "Nie udało się pobrać zapisanych zakładek.",
        });
      } finally {
        setIsLoadingBookmarks(false);
      }
    };

    void loadBookmarks();
  }, [userClient?.id]);

  useEffect(() => {
    if (!selectedBookmark.bookId || !selectedBookmark.groupName) return;

    const selectedBookmarkExists = bookmarks.some(
      (bookmark) =>
        bookmark.bookId === selectedBookmark.bookId &&
        normalizeGroupName(bookmark.groupName) ===
          normalizeGroupName(selectedBookmark.groupName),
    );

    if (!selectedBookmarkExists) {
      setSelectedBookmark({
        bookId: null,
        groupName: "",
      });
    }
  }, [bookmarks, selectedBookmark]);

  useEffect(() => {
    if (!editingBookmarkId) return;

    const editedBookmark = bookmarks.find(
      (bookmark) => bookmark.id === editingBookmarkId,
    );

    if (!editedBookmark) {
      clearRenameState();
    }
  }, [bookmarks, editingBookmarkId]);

  useEffect(() => {
    if (bookmarkMessage?.type !== "success") return;

    const clearMessageTimeout = window.setTimeout(() => {
      setBookmarkMessage((currentMessage) =>
        currentMessage?.type === "success" ? null : currentMessage,
      );
    }, 3000);

    return () => {
      window.clearTimeout(clearMessageTimeout);
    };
  }, [bookmarkMessage]);

  const handleCreateBookGroup = async (bookId: string, groupName: string) => {
    if (!userClient?.id || !bookId) {
      throw new Error("missing-book-context");
    }

    if (isElectronicBookmarkGroupNameTooLong(groupName)) {
      setBookmarkMessage({
        type: "error",
        text: ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_TEXT,
      });
      throw new Error(ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_ERROR);
    }

    const normalizedGroup = normalizeGroupName(groupName);
    const existingGroup = bookmarks.find(
      (bookmark) =>
        bookmark.clientId === userClient.id &&
        bookmark.bookId === bookId &&
        bookmark.groupNameNormalized === normalizedGroup,
    );

    if (existingGroup) {
      setBookmarkMessage({
        type: "warning",
        text: DUPLICATE_GROUP_WARNING_TEXT,
      });
      throw new Error("group-already-exists");
    }

    setIsCreatingGroup(true);

    try {
      setBookmarkMessage(null);
      const createdGroup = await upsertElectronicBookmark({
        clientId: userClient.id,
        groupName,
        bookId,
        pageNum: null,
        updatedBy: ctx.user?.email ?? undefined,
      });

      setBookmarks((prev) => {
        const filtered = prev.filter(
          (bookmark) => bookmark.id !== createdGroup.id,
        );
        return [...filtered, createdGroup];
      });
      setSelectedBookmark({
        bookId,
        groupName: createdGroup.groupName,
      });
      setBookmarkMessage({
        type: "success",
        text: `Utworzono grupę „${createdGroup.groupName}”.`,
      });
    } catch (error) {
      if (
        (error as Error).message ===
        ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_ERROR
      ) {
        throw error;
      }

      if ((error as Error).message === "group-already-exists") {
        throw error;
      }

      console.error("Błąd tworzenia grupy zakładki:", error);
      setBookmarkMessage({
        type: "error",
        text: "Nie udało się utworzyć grupy.",
      });
      throw error;
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const handleStartRename = (bookmark: ElectronicBookmark) => {
    setEditingBookmarkId(bookmark.id);
    setRenameDraft(bookmark.groupName);
    setBookmarkMessage(null);
  };

  const handleRenameBookGroup = async (bookmark: ElectronicBookmark) => {
    const cleanedGroupName = renameDraft.trim().replace(/\s+/g, " ");

    if (isElectronicBookmarkGroupNameTooLong(renameDraft)) {
      setBookmarkMessage({
        type: "error",
        text: ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_TEXT,
      });
      return;
    }

    if (!cleanedGroupName) {
      setBookmarkMessage({
        type: "warning",
        text: EMPTY_GROUP_NAME_WARNING_TEXT,
      });
      return;
    }

    if (cleanedGroupName === bookmark.groupName) {
      clearRenameState();
      return;
    }

    setPendingRenameId(bookmark.id);

    try {
      setBookmarkMessage(null);
      const renamedBookmark = await renameElectronicBookmark({
        bookmark,
        nextGroupName: cleanedGroupName,
        updatedBy: ctx.user?.email ?? undefined,
      });

      setBookmarks((prev) => {
        const filtered = prev.filter(
          (item) => item.id !== bookmark.id && item.id !== renamedBookmark.id,
        );
        return [...filtered, renamedBookmark];
      });

      if (
        selectedBookmark.bookId === bookmark.bookId &&
        normalizeGroupName(selectedBookmark.groupName) ===
          normalizeGroupName(bookmark.groupName)
      ) {
        setSelectedBookmark({
          bookId: renamedBookmark.bookId,
          groupName: renamedBookmark.groupName,
        });
      }

      clearRenameState();
      setBookmarkMessage({
        type: "success",
        text: `Zmieniono nazwę grupy na „${renamedBookmark.groupName}”.`,
      });
    } catch (error) {
      const renameError = error as Error;

      if (renameError.message === "group-already-exists") {
        setBookmarkMessage({
          type: "warning",
          text: DUPLICATE_GROUP_WARNING_TEXT,
        });
        return;
      }

      if (renameError.message === "invalid-group-name") {
        setBookmarkMessage({
          type: "warning",
          text: EMPTY_GROUP_NAME_WARNING_TEXT,
        });
        return;
      }

      if (
        renameError.message === ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_ERROR
      ) {
        setBookmarkMessage({
          type: "error",
          text: ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_TEXT,
        });
        return;
      }

      console.error("Błąd zmiany nazwy grupy zakładki:", error);
      setBookmarkMessage({
        type: "error",
        text: "Nie udało się zmienić nazwy grupy.",
      });
    } finally {
      setPendingRenameId(null);
    }
  };

  const handleDeleteBookGroup = async () => {
    if (!bookmarkPendingDelete) return;

    const bookmarkToDelete = bookmarkPendingDelete;
    setPendingDeleteId(bookmarkToDelete.id);

    try {
      setBookmarkMessage(null);
      await deleteElectronicBookmark(bookmarkToDelete.id);
      setBookmarks((prev) =>
        prev.filter((bookmark) => bookmark.id !== bookmarkToDelete.id),
      );

      if (
        selectedBookmark.bookId === bookmarkToDelete.bookId &&
        normalizeGroupName(selectedBookmark.groupName) ===
          normalizeGroupName(bookmarkToDelete.groupName)
      ) {
        setSelectedBookmark({
          bookId: null,
          groupName: "",
        });
      }

      if (editingBookmarkId === bookmarkToDelete.id) {
        clearRenameState();
      }

      setBookmarkPendingDelete(null);
      setBookmarkMessage({
        type: "success",
        text: `Usunięto grupę „${bookmarkToDelete.groupName}”.`,
      });
    } catch (error) {
      console.error("Błąd usuwania grupy zakładki:", error);
      setBookmarkMessage({
        type: "error",
        text: "Nie udało się usunąć grupy.",
      });
    } finally {
      setPendingDeleteId(null);
    }
  };

  const renderManagementBookmarkCard = ({
    bookmark,
    accentColor,
    isActive,
    onSelect,
  }: BookmarkCardRenderArgs) => {
    return (
      <BookmarkManagementCard
        title={bookmark.groupName}
        accentColor={accentColor}
        isActive={isActive}
        isEditing={editingBookmarkId === bookmark.id}
        renameDraft={
          editingBookmarkId === bookmark.id ? renameDraft : bookmark.groupName
        }
        isRenaming={pendingRenameId === bookmark.id}
        isDeleting={pendingDeleteId === bookmark.id}
        onSelect={onSelect}
        onStartEdit={() => handleStartRename(bookmark)}
        onRenameDraftChange={(value) => {
          setRenameDraft(value);
          setBookmarkMessage(null);
        }}
        onSaveRename={() => void handleRenameBookGroup(bookmark)}
        onCancelRename={clearRenameState}
        onDelete={() => {
          clearRenameState();
          setBookmarkPendingDelete(bookmark);
        }}
      />
    );
  };

  return (
    <>
      <div className="mx-auto my-2 max-w-6xl space-y-5">
        <div className="rounded-[24px] border border-white/85 bg-white/5 p-5 shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md sm:p-6">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-600 sm:hidden">
            Zarządzaj grupami
          </p>
          <p className="mb-2 hidden text-xs font-bold uppercase tracking-[0.18em] text-slate-600 sm:block">
            Zmień nazwę, dodaj lub usuń grupę
          </p>
          <p className="text-sm leading-7 text-slate-600 sm:hidden">
            Wybierz książkę a następnie edytuj przypisane do niej grupy.
          </p>
          <p className="hidden text-sm leading-7 text-slate-600 sm:block">
            W tej sekcji znajdują się grupy przypisane do poszczególnych
            podręczników, do których zachwowana została zakładka. Poza
            dodawaniem nowych, możesz zmieniać nazwę istniejących czy je usuwać.
          </p>
        </div>

        <div className="overflow-hidden rounded-[24px] border border-white/85 bg-white/5 shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md">
          <BookmarkGroupsBrowser
            activeGroupName={selectedBookmark.groupName}
            selectedBookId={selectedBookmark.bookId}
            bookmarkGroups={bookmarkGroups}
            onCreateGroup={handleCreateBookGroup}
            isCreatingGroup={isCreatingGroup}
            onOpenSavedBookmark={(bookmark) =>
              setSelectedBookmark({
                bookId: bookmark.bookId,
                groupName: bookmark.groupName,
              })
            }
            isLoadingBookmarks={isLoadingBookmarks}
            onClearMessage={() =>
              setBookmarkMessage((prev) =>
                prev?.type === "warning" ? null : prev,
              )
            }
            bookmarkMessage={bookmarkMessage}
            showBookmarkIcon={false}
            showGroupHeading={false}
            className="lg:min-h-[640px]"
            renderBookmarkCard={renderManagementBookmarkCard}
            createGroupMessagePlacement="inline"
            createGroupControlsRowClassName="max-w-[calc(35ch+3.5rem)] shrink-0"
            createGroupInputClassName="max-w-[35ch]"
            actionButtonStyleVariant="admin"
            onVisibleBookChange={() => {
              if (editingBookmarkId) {
                clearRenameState();
              }
            }}
          />
        </div>
      </div>

      {bookmarkPendingDelete && (
        <ModalConfirm
          message={`Czy na pewno chcesz usunąć grupę „${bookmarkPendingDelete.groupName}”?`}
          onCancel={() => {
            if (!pendingDeleteId) {
              setBookmarkPendingDelete(null);
            }
          }}
          onConfirm={() => void handleDeleteBookGroup()}
          cancelLabel="Anuluj"
          confirmLabel="Usuń grupę"
          isConfirming={pendingDeleteId === bookmarkPendingDelete.id}
          variant="bookmark"
          heading="Usuń grupę"
          icon={<Presentation size={24} strokeWidth={2.1} aria-hidden="true" />}
        />
      )}
    </>
  );
};

export default Groups;
